/* =============================================================================
   Mock API
   -----------------------------------------------------------------------------
   Stands in for a real backend. It is NOT a client-side array that the table
   slices — every concern the contract assigns to the server happens here:
   filtering, sorting, pagination clamping and aggregation.

   It also reproduces the parts of a real backend that are easy to forget and
   that the components must survive: network latency, cancellation, a stale
   response arriving out of order, and hard failure.
   ============================================================================= */

import type {
  AggregationOp,
  AggregationSpec,
  FilterSpec,
  RawCellValue,
  RemoteOption,
  RemoteSearchResult,
  Row,
  SortSpec,
  TableQuery,
  TableResult,
} from './types';
import { CITY_OPTIONS, CONTRACTOR_OPTIONS, CONTRACTORS } from './fixtures';

/** Set from the showcase UI so the table's error state can be demonstrated. */
export type FailureMode = 'none' | 'always' | 'once';

let failureMode: FailureMode = 'none';

export function setFailureMode(mode: FailureMode): void {
  failureMode = mode;
}

/* -----------------------------------------------------------------------------
   Cancellation and latency
   -------------------------------------------------------------------------- */

export class AbortError extends Error {
  constructor() {
    super('Request aborted');
    this.name = 'AbortError';
  }
}

export class ServerError extends Error {
  constructor(message = 'The data source failed to respond.') {
    super(message);
    this.name = 'ServerError';
  }
}

/** Awaits `ms`, rejecting with AbortError if the signal fires first. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new AbortError());
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeout(timer);
      reject(new AbortError());
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Varies latency per request. A newer request can therefore resolve before an
 * older one, which is exactly the out-of-order case the table's cancellation
 * has to defend against.
 */
function latencyFor(seed: number): number {
  return 220 + ((seed * 137) % 380);
}

let requestCounter = 0;

/* -----------------------------------------------------------------------------
   Predicate evaluation
   -------------------------------------------------------------------------- */

function asNumber(value: RawCellValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asComparable(value: RawCellValue): string | number | boolean | null {
  if (value === null || value === undefined) return null;
  return value;
}

function matchesFilter(row: Row, filter: FilterSpec): boolean {
  const cell = row[filter.field];
  const { op, value } = filter;

  switch (op) {
    case 'isNull':
      return cell === null || cell === undefined || cell === '';
    case 'isNotNull':
      return cell !== null && cell !== undefined && cell !== '';

    case 'eq':
      if (typeof cell === 'boolean' || typeof value === 'boolean') return cell === value;
      if (typeof cell === 'number') return asNumber(value as RawCellValue) === cell;
      return String(cell) === String(value);

    case 'ne':
      return !matchesFilter(row, { ...filter, op: 'eq' });

    case 'contains':
      return String(cell ?? '')
        .toLowerCase()
        .includes(String(value ?? '').toLowerCase());

    case 'startsWith':
      return String(cell ?? '')
        .toLowerCase()
        .startsWith(String(value ?? '').toLowerCase());

    case 'endsWith':
      return String(cell ?? '')
        .toLowerCase()
        .endsWith(String(value ?? '').toLowerCase());

    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = asNumber(cell);
      const b = asNumber(value as RawCellValue);
      if (a === null || b === null) {
        // Fall back to string/date comparison when either side is not numeric.
        const sa = String(cell ?? '');
        const sb = String(value ?? '');
        if (op === 'gt') return sa > sb;
        if (op === 'gte') return sa >= sb;
        if (op === 'lt') return sa < sb;
        return sa <= sb;
      }
      if (op === 'gt') return a > b;
      if (op === 'gte') return a >= b;
      if (op === 'lt') return a < b;
      return a <= b;
    }

    case 'between': {
      if (!Array.isArray(value) || value.length !== 2) return true;
      const [lo, hi] = value as [unknown, unknown];
      // An unfilled bound must not filter anything out.
      if (lo === null || lo === undefined || lo === '') {
        const b = asNumber(hi as RawCellValue);
        return b === null ? true : (asNumber(cell) ?? -Infinity) <= b;
      }
      if (hi === null || hi === undefined || hi === '') {
        const a = asNumber(lo as RawCellValue);
        return a === null ? true : (asNumber(cell) ?? Infinity) >= a;
      }
      const cellNum = asNumber(cell);
      const loNum = asNumber(lo as RawCellValue);
      const hiNum = asNumber(hi as RawCellValue);
      if (cellNum !== null && loNum !== null && hiNum !== null) {
        return cellNum >= loNum && cellNum <= hiNum;
      }
      const s = String(cell ?? '');
      return s >= String(lo) && s <= String(hi);
    }

    case 'in':
      if (!Array.isArray(value)) return true;
      if (value.length === 0) return true;
      return value.some((member) => String(member) === String(cell));

    default:
      return true;
  }
}

/* -----------------------------------------------------------------------------
   Sorting
   -------------------------------------------------------------------------- */

function compareRows(a: Row, b: Row, sort: SortSpec): number {
  const av = asComparable(a[sort.field]);
  const bv = asComparable(b[sort.field]);

  // Nulls always sort last, regardless of direction, so an empty cell never
  // floats to the top of an ascending sort.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    result = av - bv;
  } else if (typeof av === 'boolean' && typeof bv === 'boolean') {
    result = Number(av) - Number(bv);
  } else {
    result = String(av).localeCompare(String(bv), 'en', { numeric: true, sensitivity: 'base' });
  }

  return sort.dir === 'asc' ? result : -result;
}

/* -----------------------------------------------------------------------------
   Aggregation
   -------------------------------------------------------------------------- */

function computeAggregation(rows: Row[], spec: AggregationSpec): number | null {
  const values = rows
    .map((row) => row[spec.field])
    .filter((v): v is string | number => typeof v === 'number' || typeof v === 'string');

  if (values.length === 0) return null;

  const round = (n: number): number => {
    if (spec.precision === undefined) return n;
    const factor = 10 ** spec.precision;
    return Math.round(n * factor) / factor;
  };

  const op: AggregationOp = spec.op;

  if (op === 'count') return rows.length;

  if (op === 'countDistinct') {
    return new Set(values.map((v) => String(v))).size;
  }

  const numbers = values
    .map((v) => asNumber(v as RawCellValue))
    .filter((n): n is number => n !== null);

  if (numbers.length === 0) return null;

  switch (op) {
    case 'sum':
      return round(numbers.reduce((acc, n) => acc + n, 0));
    case 'avg':
      return round(numbers.reduce((acc, n) => acc + n, 0) / numbers.length);
    case 'min':
      return Math.min(...numbers);
    case 'max':
      return Math.max(...numbers);
    case 'median': {
      const sorted = [...numbers].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const value =
        sorted.length % 2 === 0
          ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
          : (sorted[mid] ?? 0);
      return round(value);
    }
    case 'stddev': {
      const mean = numbers.reduce((acc, n) => acc + n, 0) / numbers.length;
      const variance =
        numbers.reduce((acc, n) => acc + (n - mean) ** 2, 0) / numbers.length;
      return round(Math.sqrt(variance));
    }
    default:
      return null;
  }
}

/* -----------------------------------------------------------------------------
   The data source the table consumes
   -------------------------------------------------------------------------- */

/**
 * The query itself, with no transport and no latency — filters, sorts,
 * aggregates and paginates the fixture. Exported so the MSW handlers used by
 * Storybook can serve exactly the same answers over HTTP that the in-process
 * data source returns, rather than a second implementation that could drift.
 */
export function runContractorQuery(query: TableQuery): TableResult {
  // A field carries at most one active filter; a later filter on the same field
  // replaces the earlier one rather than compounding.
  const effectiveFilters = new Map<string, FilterSpec>();
  for (const filter of query.filters) effectiveFilters.set(filter.field, filter);

  const filtered = CONTRACTORS.filter((row) =>
    [...effectiveFilters.values()].every((filter) => matchesFilter(row, filter)),
  );

  const sorted = query.sort ? [...filtered].sort((a, b) => compareRows(a, b, query.sort!)) : filtered;

  // Aggregations are computed over the ENTIRE filtered set, never the page.
  const aggregations: Record<string, number | null> = {};
  for (const spec of query.aggregations) {
    aggregations[spec.field] = computeAggregation(filtered, spec);
  }

  const total = sorted.length;
  const lastPage = Math.max(1, Math.ceil(total / query.pageSize));
  const page = Math.min(Math.max(query.page, 1), lastPage);
  const start = (page - 1) * query.pageSize;
  const rows = sorted.slice(start, start + query.pageSize);

  return { rows, total, page, pageSize: query.pageSize, aggregations };
}

/**
 * The in-process data source the showcase wires the table to. Adds latency and
 * the injectable failure used to demonstrate the error state, then delegates to
 * `runContractorQuery`.
 */
export async function fetchContractors(
  query: TableQuery,
  signal: AbortSignal,
): Promise<TableResult> {
  requestCounter += 1;
  await sleep(latencyFor(requestCounter), signal);

  if (failureMode === 'always' || failureMode === 'once') {
    if (failureMode === 'once') failureMode = 'none';
    throw new ServerError();
  }

  return runContractorQuery(query);
}

/* -----------------------------------------------------------------------------
   HTTP data source — the same contract over the wire.
   Storybook's MSW handlers serve `/api/contractors`, so stories exercise the
   real request path (serialise a query, abort in flight, parse a response)
   instead of calling a function in the same process.
   -------------------------------------------------------------------------- */

export async function fetchContractorsHttp(
  query: TableQuery,
  signal: AbortSignal,
): Promise<TableResult> {
  const response = await fetch('/api/contractors', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(query),
    signal,
  });

  if (!response.ok) {
    throw new ServerError(`Data source responded ${response.status}.`);
  }

  return (await response.json()) as TableResult;
}

/* -----------------------------------------------------------------------------
   Remote search for the async combobox
   -------------------------------------------------------------------------- */

export const MIN_SEARCH_LENGTH = 2;

export async function searchContractors(
  term: string,
  signal: AbortSignal,
): Promise<RemoteSearchResult> {
  requestCounter += 1;
  await sleep(latencyFor(requestCounter), signal);

  const needle = term.trim().toLowerCase();

  if (needle.length < MIN_SEARCH_LENGTH) return { options: [], truncated: false };

  const all = CONTRACTOR_OPTIONS;
  const matched = all.filter(
    (option) =>
      option.label.toLowerCase().includes(needle) ||
      (option.description ?? '').toLowerCase().includes(needle),
  );

  return {
    options: matched.slice(0, 40),
    truncated: matched.length > 40,
  };
}

/** City search — a second remote source so the combobox demo is not a one-off. */
export async function searchCities(
  term: string,
  signal: AbortSignal,
): Promise<RemoteSearchResult> {
  requestCounter += 1;
  await sleep(latencyFor(requestCounter), signal);

  const needle = term.trim().toLowerCase();
  if (needle.length < 1) return { options: CITY_OPTIONS.slice(0, 12), truncated: false };

  const matched = CITY_OPTIONS.filter((option: RemoteOption) =>
    option.label.toLowerCase().includes(needle),
  );
  return { options: matched.slice(0, 20), truncated: matched.length > 20 };
}

/* -----------------------------------------------------------------------------
   A deliberately flaky source, used by the adversarial demo to prove the table
   discards a stale response rather than rendering it.
   -------------------------------------------------------------------------- */

export async function fetchContractorsFlaky(
  query: TableQuery,
  signal: AbortSignal,
): Promise<TableResult> {
  const fail = requestCounter % 5 === 0;
  requestCounter += 1;
  if (fail && failureMode === 'none') {
    await sleep(latencyFor(requestCounter), signal);
    throw new ServerError('Transient failure from the data source.');
  }
  return fetchContractors(query, signal);
}
